<template>
    <div class="line-chart" :style="{ height: `${height}px` }">
        <canvas ref="canvas"></canvas>
    </div>
</template>

<script>
import Chart from 'chart.js';

export default {
    name: 'LineChart',

    props: {
        labels: { type: Array, default: () => [] },
        datasets: { type: Array, default: () => [] },
        height: { type: Number, default: 220 },
        unidade: { type: String, default: '' },
        stacked: { type: Boolean, default: false },
        area: { type: Boolean, default: false },
        max: { type: Number, default: null },
    },

    data: () => ({
        chart: null,
        pendente: false,
        observer: null,
    }),

    mounted() {
        this.render();

        // Redesenha quando o container ganha caixa (voltar de outra aba, por exemplo).
        if (window.ResizeObserver) {
            this.observer = new ResizeObserver(() => {
                if (this.pendente) this.render();
            });
            this.observer.observe(this.$el);
        }
    },

    beforeDestroy() {
        if (this.observer) this.observer.disconnect();
        if (this.chart) this.chart.destroy();
    },

    watch: {
        labels() { this.render(); },
        datasets: { handler() { this.render(); }, deep: true },
        '$vuetify.theme.dark'() { this.render(); },
    },

    methods: {
        render() {
            const ctx = this.$refs.canvas;
            if (!ctx) return;

            // Aba escondida mede zero: o chart.js gravaria um canvas de altura 0 que não
            // se recupera sozinho quando a aba volta. Adia o desenho até existir caixa.
            if (!this.$el.clientHeight) {
                this.pendente = true;
                return;
            }

            this.pendente = false;
            if (this.chart) this.chart.destroy();

            const escuro = this.$vuetify.theme.dark;
            const corTexto = escuro ? '#bbb' : '#666';
            const corGrade = escuro ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)';

            const styledDatasets = this.datasets.map((ds, i) => ({
                label: ds.label,
                data: ds.data,
                borderColor: ds.color || this.defaultColor(i, 1),
                backgroundColor: ds.color
                    ? ds.color.replace(', 1)', ', 0.15)')
                    : this.defaultColor(i, 0.15),
                borderWidth: ds.tracejada ? 1.5 : 2,
                borderDash: ds.tracejada ? [5, 4] : [],
                fill: ds.fill !== undefined ? ds.fill : this.area,
                // buraco de coleta tem que aparecer como buraco, não virar uma reta que mente
                spanGaps: false,
                lineTension: 0.25,
                pointRadius: 0,
                pointHoverRadius: 4,
            }));

            this.chart = new Chart(ctx, {
                type: 'line',
                data: {
                    labels: this.labels,
                    datasets: styledDatasets,
                },
                options: {
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: { duration: 0 },
                    legend: {
                        position: 'top',
                        labels: { boxWidth: 12, fontSize: 11, fontColor: corTexto },
                    },
                    hover: { mode: 'index', intersect: false },
                    tooltips: {
                        mode: 'index',
                        intersect: false,
                        callbacks: {
                            label: (item, chartData) => {
                                const ds = chartData.datasets[item.datasetIndex];
                                if (item.yLabel === null) return `${ds.label}: sem dados`;
                                return `${ds.label}: ${this.formatar(item.yLabel)}`;
                            },
                        },
                    },
                    scales: {
                        xAxes: [{
                            gridLines: { display: false, color: corGrade },
                            ticks: {
                                maxTicksLimit: 8,
                                autoSkip: true,
                                maxRotation: 0,
                                fontSize: 10,
                                fontColor: corTexto,
                            },
                        }],
                        yAxes: [{
                            stacked: this.stacked,
                            gridLines: { color: corGrade, zeroLineColor: corGrade },
                            ticks: {
                                beginAtZero: true,
                                max: this.max || undefined,
                                fontSize: 10,
                                fontColor: corTexto,
                                callback: v => this.formatar(v),
                            },
                        }],
                    },
                },
            });
        },

        formatar(valor) {
            const v = Number(valor);
            const n = v >= 100 || Number.isInteger(v) ? v.toFixed(0) : v.toFixed(1);
            return this.unidade ? `${n}${this.unidade === '%' ? '' : ' '}${this.unidade}` : n;
        },

        defaultColor(i, alpha) {
            const palette = [
                `rgba(25, 118, 210, ${alpha})`,
                `rgba(76, 175, 80, ${alpha})`,
                `rgba(255, 152, 0, ${alpha})`,
                `rgba(156, 39, 176, ${alpha})`,
                `rgba(244, 67, 54, ${alpha})`,
            ];
            return palette[i % palette.length];
        },
    },
};
</script>

<style scoped>
.line-chart {
    position: relative;
}
</style>
